import React from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useConsent } from '../contexts/ConsentContext';

/**
 * Global, slim consent bar. Shows once until the visitor makes a choice.
 * "Nur notwendige" (deny) and "Externe Inhalte erlauben" (allow) are given
 * equal visual weight so rejecting is as easy as accepting.
 */
const ConsentBanner: React.FC = () => {
  const { externalMedia, allow, deny } = useConsent();

  return (
    <AnimatePresence>
      {externalMedia === 'unknown' && (
        <motion.div
          role="dialog"
          aria-live="polite"
          aria-label="Einwilligung zu externen Inhalten"
          initial={{ y: '110%' }}
          animate={{ y: 0 }}
          exit={{ y: '110%' }}
          transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
          style={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 3000,
            background: 'var(--color-dark, #1a1a1a)',
            color: 'rgba(255,255,255,0.9)',
            borderTop: '3px solid var(--color-brand, #FC5000)',
            padding: '1rem 1.25rem',
            boxShadow: '0 -8px 30px rgba(0,0,0,0.25)',
          }}
        >
          <div
            style={{
              maxWidth: '1100px',
              margin: '0 auto',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: '1rem 1.5rem',
              justifyContent: 'space-between',
            }}
          >
            <p
              style={{
                margin: 0,
                fontSize: '0.85rem',
                lineHeight: 1.55,
                flex: '1 1 320px',
                minWidth: '260px',
              }}
            >
              Diese Website bindet externe Inhalte von Google ein (Google&nbsp;Maps,
              YouTube-Video). Dabei können Daten – etwa Ihre IP-Adresse – an Google
              übertragen und Cookies gesetzt werden. Diese Inhalte werden erst nach
              Ihrer Einwilligung geladen.{' '}
              <Link to="/datenschutz" style={{ color: '#fff', textDecoration: 'underline' }}>
                Datenschutzerklärung
              </Link>
            </p>

            <div style={{ display: 'flex', gap: '0.75rem', flex: '0 0 auto' }}>
              <button
                type="button"
                onClick={deny}
                style={{
                  padding: '0.6rem 1.1rem',
                  fontSize: '0.82rem',
                  fontWeight: 600,
                  color: 'rgba(255,255,255,0.9)',
                  background: 'transparent',
                  border: '1px solid rgba(255,255,255,0.4)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                Nur notwendige
              </button>
              <button
                type="button"
                onClick={allow}
                style={{
                  padding: '0.6rem 1.3rem',
                  fontSize: '0.82rem',
                  fontWeight: 700,
                  color: '#fff',
                  background: 'var(--color-brand, #FC5000)',
                  border: '1px solid var(--color-brand, #FC5000)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                Externe Inhalte erlauben
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ConsentBanner;
